import * as Pipeline from '../core/utils/pipeline';
import {
  AdsHorizontalTabsErrors,
  HorizontalTab,
  TabBlurEvent,
  TabBlurHandler,
  TabCloseEvent,
  TabCloseHandler,
  TabFocusEvent,
  TabFocusHandler,
  TabSelectEvent,
  TabSelectHandler,
} from './ads-horizontal-tabs.types';
import { AdsHorizontalTabsTab } from './components/ads-horizontal-tabs-tab/ads-horizontal-tabs-tab';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Breakpoint, Breakpoints } from '../core/types/globalTypes';
import { Component, Element, Event, EventEmitter, Listen, Method, Prop, State, VNode, Watch, h } from '@stencil/core';
import { Theme } from '../core/types/globalTypes';
import { generateId, getMediaQueryBreakpointClassUp, showErrorMessage } from '../core/utils/components';
import {
  getHtmlDirObserver,
  getSlottedElements,
  isKeyboardClickEvent,
  isNil,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  setFocusOnArea,
} from 'utils/index';

/**
 * Tabs are used to navigate between views within the same context. Tabs are best used to separate content into smaller groups of related information. As default one tab will already be selected and its related content will be visible. Only one tab can be selected at a time.
 * @tag `<ads-horizontal-tabs>`
 * @slot content - The content associated with a tab
 * @example <ads-horizontal-tabs><div slot="content" data-id="slot-1">slot-1 text</div><div slot="content" data-id="slot-2">slot-2 text</div></ads-horizontal-tabs>
 */
@Component({
  tag: 'ads-horizontal-tabs',
  styleUrl: 'ads-horizontal-tabs.scss',
  shadow: true,
})
export class AdsHorizontalTabs implements BaseComponent {
  public component: string;

  private firstRender = true;

  private mutationObs: MutationObserver;

  private availablePanelIds: Set<string> = new Set();

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-horizontal-tabs'));
    this.component = 'ads-horizontal-tabs';
  }

  @Element() private el: HTMLAdsHorizontalTabsElement;

  /**
   * @hidden
   */
  @State() activeTabInternal: string;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The dir attribute (optional)
   */
  @Prop({ attribute: 'dir' }) direction?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * The breakpoint for the components responsiveness (optional)
   * @default "xl"
   */
  @Prop({ reflect: true }) breakpoint: Breakpoint = Breakpoints.XL;

  /**
   * The currently active tab (optional)
   */
  @Prop({ reflect: true }) activeTab?: string;

  /**
   * The data that defines the tabs
   */
  @Prop() data: HorizontalTab[];

  /**
   * Text to display as the aria-label of the component (optional)
   */
  @Prop({ reflect: true }) srLabel?: string;

  /**
   * Callback function triggered on tab selection (optional)
   */
  @Prop() tabSelectHandler?: TabSelectHandler;

  /**
   * Callback function triggered on tab blur (optional)
   */
  @Prop() tabBlurHandler?: TabBlurHandler;

  /**
   * Callback function triggered on tab close (optional)
   */
  @Prop() tabCloseHandler?: TabCloseHandler;

  /**
   * Callback function triggered on tab focus (optional)
   */
  @Prop() tabFocusHandler?: TabFocusHandler;

  /**
   * Property to enable tab animation (optional)
   */

  @Prop({ reflect: true }) enableTabAnimation?: boolean = false;

  /**
   * Emits 'tabBlur' custom event.
   * Usage: `document.querySelector('ads-horizontal-tabs').addEventListener('tabBlur', function(evt) {});`
   * @event
   */
  @Event({ bubbles: false }) tabBlur: EventEmitter<TabBlurEvent>;

  /**
   * Emits 'tabClose' custom event.
   * Usage: `document.querySelector('ads-horizontal-tabs').addEventListener('tabClose', function(evt) {});`
   * @event
   */
  @Event({ bubbles: false }) tabClose: EventEmitter<TabCloseEvent>;

  /**
   * Emits 'tabFocus' custom event.
   * Usage: `document.querySelector('ads-horizontal-tabs').addEventListener('tabFocus', function(evt) {});`
   * @event
   */
  @Event({ bubbles: false }) tabFocus: EventEmitter<TabFocusEvent>;

  /**
   * Emits 'tabSelect' custom event.
   * Usage: `document.querySelector('ads-horizontal-tabs').addEventListener('tabSelect', function(evt) {});`
   * @event
   */
  @Event({ bubbles: false }) tabSelect: EventEmitter<TabSelectEvent>;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-horizontal-tabs'));
  }

  /**
   * Handle direction property changes
   */
  @Watch('direction')
  handleDirectionChange(): void {
    this.onDirChange();
  }

  /**
   * Listen to tabSelect event. (triggered by the tab component)
   * @param {CustomEvent} evt the selection event
   * @returns {void}
   * @hidden
   */
  @Listen('tabSelect')
  protected onTabSelected(evt: CustomEvent<TabSelectEvent>): void {
    if (isNullOrUndefinedOrEmpty(this.activeTab)) {
      this.changeActiveTab(evt.detail.id, isKeyboardClickEvent(evt.detail.evt));
    }
  }

  /**
   * Watch for activeTab prop change.
   * @param {string} newValue the new prop value
   * @returns {void}
   * @hidden
   */
  @Watch('activeTab')
  onActiveTabChanged(newValue: string): void {
    if (!isNullOrUndefinedOrEmpty(newValue)) {
      this.changeActiveTab(newValue);
    }
  }

  /**
   * Watch for data prop change.
   * @param {HorizontalTab[]} newValue the new prop value
   * @returns {void}
   * @hidden
   */
  @Watch('data')
  onDataChanged(newValue: HorizontalTab[]): void {
    if (isNullOrUndefined(newValue)) {
      return;
    }
    if (
      isNullOrUndefinedOrEmpty(this.activeTab) &&
      (isNullOrUndefinedOrEmpty(this.activeTabInternal) || newValue.every((tab: HorizontalTab) => tab.id !== this.activeTabInternal))
    ) {
      this.setActiveTabState(this.getFirstTabIdentifier(newValue));
    } else {
      setTimeout(() => {
        this.updateActiveLine();
      }, 100);
    }
  }

  /**
   * Returns the id of the current active tab
   * @return {Promise<string>}  The active tab id
   */
  @Method()
  value(): Promise<string> {
    return Promise.resolve(this.activeTabInternal);
  }

  /**
   * Updates underline with transition
   */
  private onDirChange = () => {
    const navigationEl = this.getNavigationContainer();
    navigationEl?.classList.add('no-transition');
    this.updateActiveLine();
    setTimeout(() => {
      navigationEl?.classList.remove('no-transition');
    }, 50);
  };

  /**
   * Gets the first tab id
   * @param {HorizontalTab[]} data the data to go through
   * @returns {string} the fist id
   */
  private getFirstTabIdentifier = (data: HorizontalTab[]): string => {
    return !isNullOrUndefined(data) && !isNil(data.length) ? data[0].id : '';
  };

  /**
   * Sets the active tab state
   * @param {string} newActiveTab The new active tab value
   */
  private setActiveTabState = (newActiveTab: string): void => {
    this.activeTabInternal = newActiveTab;
    this.updateActiveLine();
  };

  /**
   * Update the css props that drive the underline
   */
  private updateActiveLine = (): void => {
    const activeTab = this.getActiveTabElement();
    if (!isNullOrUndefined(activeTab)) {
      const activeTabClientRect = activeTab.getBoundingClientRect();
      const $container = this.el.shadowRoot.querySelector('.c-horizontal-tabs__navigation');
      this.el.style.setProperty('--ads-horizontal-tabs-active-left', `${activeTabClientRect.left - $container.getBoundingClientRect().x}px`);
    }
  };

  /**
   * Gets the active tab element
   * @returns {Element} The active tab element
   */
  private getActiveTabElement = (): Element => this.el.shadowRoot.querySelector(`[data-slot="${this.activeTabInternal}"]`);

  /**
   * Gets the navigation container element
   * @returns {HTMLElement} The navigation container element
   */
  private getNavigationContainer = (): HTMLElement => this.el.shadowRoot.querySelector('.c-horizontal-tabs__navigation');

  /**
   * Sets the current active tab
   * @param {string} id The tab id to set as active
   * @param {boolean} focusSlottedContent Should focus slotted content
   */
  private changeActiveTab(id: string, focusSlottedContent = false): void {
    const slottedContentElements = getSlottedElements(this.el, 'content');
    const newActiveTabEl = slottedContentElements.find((slotElement: HTMLElement) => slotElement.getAttribute('data-id') === id);
    if (!isNullOrUndefined(newActiveTabEl)) {
      if (focusSlottedContent) {
        setFocusOnArea(newActiveTabEl);
      }
      this.setActiveTabState(id);
    } else {
      // if customer defined active tab doesn't exist, show error message
      if (!isNullOrUndefinedOrEmpty(this.activeTab) && !this.firstRender) {
        showErrorMessage(AdsHorizontalTabsErrors.TAB_DOESNT_EXIST, this.el);
      }
    }
  }

  /**
   * Updates the content attributes based on active tab
   */
  private updateSlottedContentActiveStates(): void {
    const $slottedContentElements = getSlottedElements(this.el, 'content');
    if (isNil($slottedContentElements.length)) {
      this.availablePanelIds.clear();
      return;
    }
    this.availablePanelIds.clear();
    $slottedContentElements.map(($slotElement: HTMLElement) => {
      const slotElementIdentifier = $slotElement.getAttribute('data-id');
      const panelId = `tabs-${this.identifier}-panel-${slotElementIdentifier}`;
      this.availablePanelIds.add(panelId);
      $slotElement.setAttribute('id', panelId);
      $slotElement.setAttribute('role', 'tabpanel');
      $slotElement.classList.remove('active');
      if (slotElementIdentifier === this.activeTabInternal) {
        $slotElement.classList.add('active');
      }
    });
  }

  protected componentWillLoad(): void {
    if (isNullOrUndefinedOrEmpty(this.activeTab)) {
      this.setActiveTabState(this.getFirstTabIdentifier(this.data));
    } else {
      this.setActiveTabState(this.activeTab);
    }
    this.mutationObs = getHtmlDirObserver(this.onDirChange);
  }

  protected componentDidLoad(): void {
    this.updateSlottedContentActiveStates();
    this.updateActiveLine();
    Pipeline.addComponent(this.el);
    this.firstRender = false;
    setTimeout(() => {
      this.getNavigationContainer()?.classList.remove('no-transition');
    }, 50);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
    this.mutationObs?.disconnect();
  }

  /**
   * Renders the tabs sub-components
   * @returns {VNode} the tab elements
   */
  private renderTabs() {
    return !isNullOrUndefined(this.data) && this.data.length > 0
      ? this.data.map((tab: HorizontalTab, index) => {
          return (
            <AdsHorizontalTabsTab
              tabsId={this.identifier}
              id={tab.id}
              isActive={tab.id === this.activeTabInternal}
              icon={tab.icon}
              label={tab.label}
              dismissible={tab.dismissible}
              isFirst={index === 0}
              isLast={this.data.length === index + 1}
              onSelect={this.tabSelect}
              selectHandler={this.tabSelectHandler}
              onBlur={this.tabBlur}
              blurHandler={this.tabBlurHandler}
              onClose={this.tabClose}
              closeHandler={this.tabCloseHandler}
              onFocus={this.tabFocus}
              focusHandler={this.tabFocusHandler}
              attributes={tab.attributes}
              availablePanelIds={this.availablePanelIds}
            />
          ) as VNode;
        })
      : null;
  }

  protected render(): VNode {
    if (isNullOrUndefined(this.data)) {
      return;
    }

    this.updateSlottedContentActiveStates();
    const $tabElements = this.renderTabs();
    const attributes = {
      'aria-label': this.srLabel,
      'aria-orientation': 'horizontal',
    };

    const cssVars: { 'background-color'?: string } = {};

    const navigationClasses = ['c-horizontal-tabs__navigation', `${getMediaQueryBreakpointClassUp(this.breakpoint)}`];
    if (this.firstRender) {
      navigationClasses.push('no-transition');
    }

    const $navigation = (
      <div class={navigationClasses.join(' ')} role="tablist" {...attributes}>
        {$tabElements}
      </div>
    ) as HTMLElement;

    // Add the animation class conditionally
    const contentClasses = ['c-horizontal-tabs__content', 'js-horizontal-tabs-content'];
    if (this.enableTabAnimation) {
      contentClasses.push('c-horizontal-tabs--animated');
    }

    const $content = (
      <div class={contentClasses.join(' ')} style={cssVars}>
        <slot name="content" />
      </div>
    ) as HTMLElement;

    return (
      <div class="c-horizontal-tabs js-horizontal-tabs">
        <div class={`c-horizontal-tabs__tab-container ${getMediaQueryBreakpointClassUp(this.breakpoint)}`}>
          <div class={`c-horizontal-tabs__navigation-wrapper ${getMediaQueryBreakpointClassUp(this.breakpoint)}`}>{$navigation}</div>
        </div>
        {$content}
      </div>
    ) as VNode;
  }
}
